import { toNumberFormatNotationShort }      from '@/lib/utils/helper'
import BaseResource                         from '@/lib/data/resource/BaseResource'
import AssetModel                           from '@/api/models/asset/AssetModel'

export default class ALMLoansResource extends BaseResource {
  constructor (payload = {}) {
    super(payload)

    this.TitleValue = payload.loans?.descriptiveStatistics?.value ?? ''
    this.TitleValueChange = payload.loans?.dynamicEvolution?.totalValueChange ?? ''
    this.BaseCurrency = new AssetModel(payload.baseCurrency ?? null)

    // Loans data
    this.Loans = payload.loans ?? {}
    this.DescriptiveStatistics = this.Loans.descriptiveStatistics ?? {}
    this.LoansMaturity = this.Loans.loansMaturity ?? {}
    this.InterestIncome = this.Loans.interestIncome ?? {}
    this.TypeAllocation = this.Loans.typeAllocation ?? {}
    this.DynamicEvolution = this.Loans.dynamicEvolution ?? {}
    this.LoansConcentration = this.Loans.loansConcentration ?? {}
    this.FixedFloatingComparison = this.Loans.fixedFloatingComparison ?? {}
  }

  /* PROPERTIES */
  get BaseCurrencyTitle () {
    return this.baseCurrencySymbolSingle() ? this.getBaseCurrencySymbolSingle() : this.getBaseCurrencySymbolSingle() + ' '
  }

  get BaseCurrencyDisplayName () {
    return this.BaseCurrency?.displayName || ''
  }

  get BaseCurrencyDisplaySymbol () {
    return this.BaseCurrency?.displaySymbol || ''
  }

  get TitleValueFmt () {
    return this.TitleValue ? toNumberFormatNotationShort(this.TitleValue) : ''
  }

  get TitleValueChangeFmt () {
    return this.TitleValueChange ? this.BaseCurrencyTitle + toNumberFormatNotationShort(this.TitleValueChange) : ''
  }

  // Descriptive Statistics
  get LoansSize () {
    return this.DescriptiveStatistics.size || 0
  }

  get LoansCount () {
    return this.DescriptiveStatistics.loans || 0
  }

  get LoansMaturityValue () {
    return this.DescriptiveStatistics.maturity || 0
  }

  get LoansMaturityUnit () {
    return this.DescriptiveStatistics.maturityUnit || ''
  }

  get LoansRepricingValue () {
    return this.DescriptiveStatistics.repricing || 0
  }

  get LoansRepricingUnit () {
    return this.DescriptiveStatistics.repricingUnit || ''
  }

  get LoansDuration () {
    return this.DescriptiveStatistics.duration || 0
  }

  get LoansDurationUnit () {
    return this.DescriptiveStatistics.durationUnit || ''
  }

  // Interest Income
  get InterestIncome3m () {
    return this.InterestIncome.interestIncome3m || 0
  }

  get InterestIncome12m () {
    return this.InterestIncome.interestIncome12m || 0
  }

  // Type Allocation
  get TypeNames () {
    return this.TypeAllocation.typeNames || []
  }

  get PerTypeAllocationData () {
    return this.TypeAllocation.perTypeAllocationData || []
  }

  get TotalAllocationData () {
    return this.TypeAllocation.totalAllocationData || []
  }

  // Dynamic Evolution
  get EvolutionDates () {
    return this.DynamicEvolution.dates || []
  }

  get EvolutionTypeNames () {
    return this.DynamicEvolution.typeNames || []
  }

  get EvolutionTotalValue () {
    return this.DynamicEvolution.totalValue || 0
  }

  get EvolutionTotalValueChange () {
    return this.DynamicEvolution.totalValueChange || 0
  }

  get EvolutionAmountOfContracts () {
    return this.DynamicEvolution.amountOfContracts || {}
  }

  get EvolutionAmountOfContractsChange () {
    return this.DynamicEvolution.amountOfContractsChange || 0
  }

  get EvolutionDuration () {
    return this.DynamicEvolution.duration || 0
  }

  get EvolutionDurationChange () {
    return this.DynamicEvolution.durationChange || 0
  }

  get EvolutionInterestIncome () {
    return this.DynamicEvolution.interestIncome || 0
  }

  get EvolutionInterestIncomeChange () {
    return this.DynamicEvolution.interestIncomeChange || 0
  }

  // Loans Maturity
  get MaturityLabels () {
    return this.LoansMaturity.labels || []
  }

  get MaturityData () {
    return this.LoansMaturity.maturityData || []
  }

  // Loans Concentration
  get ConcentrationTypeNames () {
    return this.LoansConcentration.typeNames || []
  }

  get PerTypeConcentrationData () {
    return this.LoansConcentration.perTypeConcentrationData || []
  }

  // Fixed Floating Comparison
  get FixedFloatingLabels () {
    return this.FixedFloatingComparison.labels || []
  }

  get PerLabelFixedFloatingComparisonData () {
    return this.FixedFloatingComparison.perLabelFixedFloatingComparisonData || []
  }

  /* METHODS */
  baseCurrencySymbolSingle () {
    return this.BaseCurrencyDisplaySymbol.length === 1
  }

  getBaseCurrencySymbolSingle () {
    return this.baseCurrencySymbolSingle() ? this.BaseCurrencyDisplaySymbol : this.BaseCurrencyDisplayName
  }

  // Helper method to get type allocation by type name
  getTypeAllocationByType (typeName) {
    return this.PerTypeAllocationData.find(item => item.typeName === typeName) || null
  }

  // Helper method to get concentration data by type name
  getConcentrationDataByType (typeName) {
    return this.PerTypeConcentrationData.find(item => item.typeName === typeName) || null
  }

  // Helper method to get fixed floating comparison data by label
  getFixedFloatingComparisonByLabel (label) {
    return this.PerLabelFixedFloatingComparisonData.find(item => item.label === label) || null
  }
}
